import Box from '@mui/material/Box';
import type {CheckboxProps} from '@mui/material/Checkbox';
import Checkbox from '@mui/material/Checkbox';
import type {ChipProps} from '@mui/material/Chip';
import Chip from '@mui/material/Chip';
import type {FormControlProps} from '@mui/material/FormControl';
import FormControl from '@mui/material/FormControl';
import type {FormHelperTextProps} from '@mui/material/FormHelperText';
import type {InputLabelProps} from '@mui/material/InputLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import type {SelectProps} from '@mui/material/Select';
import Select from '@mui/material/Select';
import type {TextFieldProps} from '@mui/material/TextField';
import TextField from '@mui/material/TextField';
import {merge} from 'es-toolkit';
import {ReactNode} from 'react';
import {Controller, useFormContext} from 'react-hook-form';

import {HelperText} from './help-text';

// ----------------------------------------------------------------------

type RHFSelectProps = TextFieldProps & {
  name: string;
  children: ReactNode;
};

export const RHFSelect = ({name, children, helperText, slotProps = {}, ...other}: RHFSelectProps) => {
  const {control} = useFormContext();

  const labelId = `${name}-select`;

  const baseSlotProps: TextFieldProps['slotProps'] = {
    select: {
      MenuProps: {
        slotProps: {
          paper: {
            sx: [{maxHeight: 220}],
          },
        },
      },
    },
    htmlInput: {id: labelId},
    inputLabel: {htmlFor: labelId},
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({field, fieldState: {error}}) => (
        <TextField
          {...field}
          select
          fullWidth
          error={!!error}
          helperText={error?.message ?? helperText}
          slotProps={merge(baseSlotProps, slotProps)}
          {...other}
        >
          {children}
        </TextField>
      )}
    />
  );
};

// ----------------------------------------------------------------------

type RHFMultiSelectProps = FormControlProps & {
  name: string;
  label?: string;
  chip?: boolean;
  checkbox?: boolean;
  placeholder?: string;
  helperText?: ReactNode;
  options: {label: string; value: string | number}[];
  slotProps?: {
    chip?: ChipProps;
    select?: SelectProps;
    checkbox?: CheckboxProps;
    inputLabel?: InputLabelProps;
    helperText?: FormHelperTextProps;
  };
};

export const RHFMultiSelect = ({
  name,
  chip,
  label,
  options,
  checkbox,
  placeholder,
  slotProps,
  helperText,
  ...other
}: RHFMultiSelectProps) => {
  const {control} = useFormContext();

  const labelId = `${name}-multi-select`;

  return (
    <Controller
      name={name}
      control={control}
      render={({field, fieldState: {error}}) => {
        const renderLabel = () => (
          <InputLabel htmlFor={labelId} {...slotProps?.inputLabel}>
            {label}
          </InputLabel>
        );

        const renderOptions = () =>
          options.map((option) => (
            <MenuItem key={option.value.toString()} value={option.value}>
              {checkbox && (
                <Checkbox
                  size="small"
                  disableRipple
                  checked={field.value.includes(option.value)}
                  {...slotProps?.checkbox}
                />
              )}

              {option.label}
            </MenuItem>
          ));

        return (
          <FormControl error={!!error} {...other}>
            {label && renderLabel()}

            <Select
              {...field}
              multiple
              displayEmpty={!!placeholder}
              label={label}
              renderValue={(selected) => {
                const selectedItems = options.filter((item) => (selected as (string | number)[]).includes(item.value));

                if (!selectedItems.length && placeholder) {
                  return <Box sx={{color: 'text.disabled'}}>{placeholder}</Box>;
                }

                if (chip) {
                  return (
                    <Box sx={{gap: 0.5, display: 'flex', flexWrap: 'wrap'}}>
                      {selectedItems.map((item) => (
                        <Chip
                          key={item.value}
                          size="small"
                          color="primary"
                          variant="soft"
                          label={item.label}
                          {...slotProps?.chip}
                        />
                      ))}
                    </Box>
                  );
                }

                return selectedItems.map((item) => item.label).join(', ');
              }}
              {...slotProps?.select}
              inputProps={{
                id: labelId,
                ...slotProps?.select?.inputProps,
              }}
            >
              {renderOptions()}
            </Select>

            <HelperText {...slotProps?.helperText} errorMessage={error?.message} helperText={helperText} />
          </FormControl>
        );
      }}
    />
  );
};
